import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Accessibility } from "lucide-react";

// ===============================================================
// ENGAGEMENT QUALITÉ — Fond bleu clair, bordure gauche orange
// ===============================================================
export const EngagementQualite = () => (
  <section
    className="my-12 md:my-16"
    data-testid="engagement-qualite"
  >
    <div className="container-custom">
      <div
        className="rounded-xl p-6 md:p-10 border-l-4 border-[#F07B1D]"
        style={{ backgroundColor: "#E3F2FD" }}
      >
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#003087] mb-6">
          Notre engagement qualité
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-900">
          <div>
            <h3 className="font-heading font-bold text-base mb-2">📋 Conformité Qualiopi en cours</h3>
            <p className="text-sm leading-relaxed text-gray-800">
              Nos programmes sont conçus selon le <strong>Référentiel National Qualité (Qualiopi) — Indicateur 1</strong>. La démarche de certification est en cours auprès de l'organisme certificateur compétent.
            </p>
          </div>
          <div>
            <h3 className="font-heading font-bold text-base mb-2">🎯 Pédagogie orientée compétences</h3>
            <p className="text-sm leading-relaxed text-gray-800">
              Chaque formation est construite autour d'objectifs pédagogiques mesurables (<strong>Taxonomie de Bloom</strong>). Les apprenants repartent avec des compétences directement mobilisables.
            </p>
          </div>
          <div>
            <h3 className="font-heading font-bold text-base mb-2">📊 Évaluation systématique</h3>
            <p className="text-sm leading-relaxed text-gray-800">
              Questionnaire de positionnement avant · Évaluations formatives par module · Évaluation finale sommative · Questionnaire de satisfaction à chaud et à froid (30 jours).
            </p>
          </div>
          <div>
            <h3 className="font-heading font-bold text-base mb-2">♿ Accessibilité universelle</h3>
            <p className="text-sm leading-relaxed text-gray-800">
              Référent handicap dédié · Adaptation des modalités sur demande · Format distanciel favorisant l'inclusion ·{" "}
              <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline font-medium">contact@mgbschool.fr</a>
            </p>
          </div>
          <div className="md:col-span-2">
            <h3 className="font-heading font-bold text-base mb-2">📜 Documents inclus pour les financeurs</h3>
            <p className="text-sm leading-relaxed text-gray-800">
              Convention de formation · Feuilles d'émargement · Attestation de fin de formation · Programme détaillé conforme · Facture avec mentions légales (NDA 11757727775, exonération TVA art. 261-4-4° CGI).
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ===============================================================
// BANDEAU FINANCEURS — Sur la home, entre hero et popular formations
// ===============================================================
export const BandeauFinanceurs = () => {
  const items = [
    { emoji: "🏦", label: "OPCO" },
    { emoji: "🎯", label: "France Travail" },
    { emoji: "📱", label: "CPF" },
    { emoji: "🏢", label: "Plan de formation" },
    { emoji: "🔄", label: "Reconversion Pro" },
  ];
  return (
    <section
      className="bg-white border-t border-b border-gray-200 py-8 md:py-10"
      data-testid="bandeau-financeurs"
    >
      <div className="container-custom text-center">
        <p className="text-sm md:text-base text-gray-700 font-medium mb-2">
          Organisme de formation déclaré (NDA 11757727775) — financement individuel ou plan de formation entreprise.
        </p>
        <p className="text-xs text-gray-500 mb-5 italic">
          Démarches de référencement en cours auprès des financeurs institutionnels :
        </p>
        <div className="flex flex-wrap items-start justify-center gap-4 md:gap-6 mb-6">
          {items.map((it) => (
            <div
              key={it.label}
              className="flex flex-col items-center gap-1 px-4 py-3 rounded-lg bg-[#F8FAFC] border border-gray-200 min-w-[120px]"
              data-testid={`financeur-tag-${it.label}`}
            >
              <span className="text-2xl">{it.emoji}</span>
              <span className="text-sm font-bold text-[#003087]">{it.label}</span>
              <span className="text-[11px] text-gray-500">Démarches en cours</span>
            </div>
          ))}
        </div>
        <Link to="/contact">
          <button
            type="button"
            className="bg-[#F07B1D] text-white font-bold px-6 py-3 rounded-lg hover:bg-[#D96A0F] hover:-translate-y-0.5 transition-all shadow-md inline-flex items-center gap-2"
            data-testid="check-eligibility-btn"
          >
            Suivre l'avancement des démarches
            <ArrowRight className="h-4 w-4" />
          </button>
        </Link>
      </div>
    </section>
  );
};

// ===============================================================
// BLOC ACCESSIBILITÉ — Sur chaque page de formation
// ===============================================================
export const AccessibiliteBlock = () => (
  <div
    className="rounded-xl p-6 md:p-7 border-l-4 border-[#F07B1D] flex items-start gap-4"
    style={{ backgroundColor: "#FFF3E8" }}
    data-testid="accessibility-block"
  >
    <div className="w-12 h-12 rounded-xl bg-[#F07B1D]/15 flex items-center justify-center shrink-0">
      <Accessibility className="h-6 w-6 text-[#F07B1D]" />
    </div>
    <div>
      <h3 className="font-heading text-lg font-bold text-[#003087] mb-2">
        Accessibilité et situations de handicap
      </h3>
      <p className="text-sm text-gray-800 leading-relaxed mb-3">
        MGB School s'engage à rendre ses formations accessibles à tous les publics, y compris les{" "}
        <strong>personnes en situation de handicap (PSH)</strong>. Chaque situation étant unique, notre référent handicap étudie avec vous les adaptations nécessaires avant votre entrée en formation : aménagements pédagogiques, matériels ou organisationnels.
      </p>
      <div className="text-sm text-gray-800 space-y-1">
        <p>
          <strong>Référent Handicap :</strong>{" "}
          <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline font-medium">
            contact@mgbschool.fr
          </a>
          {" · "}
          <a href="tel:+33758783574" className="text-[#F07B1D] underline font-medium">
            07 58 78 35 74
          </a>
        </p>
        <p className="text-gray-600">25 rue de Ponthieu, 75008 Paris</p>
      </div>
    </div>
  </div>
);

// ===============================================================
// INDICATEURS QUALIOPI — Page À propos (version enrichie)
// ===============================================================
export const IndicateursQualiopi = () => (
  <section
    className="my-12 md:my-16"
    data-testid="indicateurs-qualiopi-detailed"
  >
    <div className="container-custom">
      <div className="rounded-xl p-6 md:p-10" style={{ backgroundColor: "#E3F2FD" }}>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#003087] mb-4 flex items-center gap-2">
          <span>📊</span> Indicateurs de résultats
        </h2>
        <p className="text-gray-800 leading-relaxed mb-5">
          MGB School est un organisme de formation <strong>en cours de lancement</strong>. Conformément aux exigences du Référentiel National Qualité, nous publierons nos indicateurs de résultats à l'issue des premières sessions :
        </p>
        <ul className="space-y-2 mb-5">
          {[
            "Taux de satisfaction des apprenants",
            "Taux de réussite à l'évaluation finale",
            "Nombre de stagiaires formés",
            "Taux d'insertion professionnelle (pour les formations insertion)",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-gray-800">
              <span className="text-[#F07B1D] font-bold mt-0.5">→</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-gray-600 italic">
          Les données seront mises à jour après chaque session.
        </p>
      </div>
    </div>
  </section>
);
